// Host-only prompts over the stage: pick who takes over before leaving, and "are you sure?" before ending for everyone.
import { useState } from 'react';
import { Crown } from 'lucide-react';

const btn = 'rounded-lg px-4 py-2 text-sm font-semibold transition-colors duration-150 disabled:opacity-50';

function Card({ tid, children }) {
  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 p-4" data-testid={tid}>
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0D1B2A] p-5 text-white shadow-2xl">{children}</div>
    </div>
  );
}

function PickHost({ members, onPick, onSkip, onCancel }) {
  const [sel, setSel] = useState(members[0]?.user_id || '');
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); await onPick(sel); setBusy(false); };
  return (
    <Card tid="mm-handoff">
      <p className="flex items-center gap-2 text-base font-semibold"><Crown className="h-4 w-4 text-amber-300" /> Pick a new host</p>
      <p className="mt-1 text-xs text-slate-400">They&apos;ll be able to let guests in, lower hands and end the meeting.</p>
      <div className="mt-3 max-h-56 space-y-1 overflow-y-auto" role="radiogroup" aria-label="New host">
        {members.map((p) => (
          <button key={p.session_id} type="button" role="radio" aria-checked={sel === p.user_id} onClick={() => setSel(p.user_id)}
            data-testid={`mm-handoff-${p.session_id}`}
            className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 ${sel === p.user_id ? 'bg-[#4575A8]/30 ring-1 ring-[#4575A8]' : 'hover:bg-white/5'}`}>
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#4575A8] text-xs font-semibold">{(p.user_name || '?').slice(0, 1).toUpperCase()}</span>
            <span className="truncate">{p.user_name || 'Team member'}</span>
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-col gap-2">
        <button type="button" className={`${btn} bg-[#4575A8] hover:bg-[#3A6491]`} disabled={!sel || busy} onClick={go} data-testid="mm-handoff-confirm">Make host and leave</button>
        <div className="flex justify-between">
          <button type="button" className={`${btn} px-2 text-slate-300 hover:text-white`} onClick={onSkip} data-testid="mm-handoff-skip">Leave without choosing</button>
          <button type="button" className={`${btn} px-2 text-slate-400 hover:text-white`} onClick={onCancel} data-testid="mm-handoff-cancel">Cancel</button>
        </div>
      </div>
    </Card>
  );
}

function ConfirmEnd({ onEnd, onCancel }) {
  return (
    <Card tid="mm-end-confirm">
      <p className="text-base font-semibold">End the meeting for everyone?</p>
      <p className="mt-1 text-xs text-slate-400">Everyone, guests too, will be removed from the call.</p>
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className={`${btn} bg-white/10 hover:bg-white/20`} onClick={onCancel} data-testid="active-call-end-cancel">Cancel</button>
        <button type="button" className={`${btn} bg-red-600 hover:bg-red-700`} onClick={onEnd} data-testid="active-call-end-confirm">End for everyone</button>
      </div>
    </Card>
  );
}

export default function HostDialogs({ ask, setAsk, handoff, dc, onEndAll }) {
  if (ask === 'leave') {
    return (
      <PickHost members={handoff.members} onCancel={() => setAsk(null)}
        onSkip={() => { setAsk(null); handoff.leaveAsHost(); }}
        onPick={async (uid) => { if (await handoff.transfer(uid)) { setAsk(null); dc.leave(); } }} />
    );
  }
  if (ask === 'end') return <ConfirmEnd onCancel={() => setAsk(null)} onEnd={() => { setAsk(null); onEndAll(); }} />;
  return null;
}
